import standBackgroundUrl from '../assets/stand.png';
import { normalizePersonaTransformFromDb } from '../utils/personaHotspotTransform';

/**
 * Construye el array de hotSpots para Pannellum.
 * Extraído de CentroViewer.js para que el compilador no se cuelgue.
 */
export function buildHotspots(opts) {
  const {
    buildImageUrl,
    viewerRef,
    pannellumRef,
    setImagenActual,
    setInfoTienda,
    setHotspotInfo,
    onNavigateToScene,
    getSceneEntryView,
    maxHfov,
    transitionProfile = 'street',
    isMultiScene,
    centro,
    panoramas,
    hotspotsDeImagen,
    tiendasDeImagen,
    forceHotspotsVisible,
    onHotspotLayoutRefresh
  } = opts;
  /** Debe coincidir con CentroViewer.css (.pnlm-fade-img usa 100000); si no, el fundido tapa las flechas */
  const HOTSPOT_Z = '100001';
  /** Mismo valor que `default.minHfov` en CentroViewer (tour Pannellum) */
  const MIN_HFOV_NAV = 50;
  /**
   * Zoom óptico tipo tour de referencia: mira hacia el hotspot y reduce hfov; luego loadScene + fundido nativo.
   * El callback de lookAt() en Pannellum no encadena bien pitch+yaw+hfov, así que loadScene va en setTimeout(preLook).
   * Hotspots: Pannellum reposiciona el DOM en cada frame; mantener forceHotspotsVisible + z-index HOTSPOT_Z.
   */
  let navOpticalTimer = null;
  /** Prioridad de red + decodificación antes de loadScene (el cuello de botella suele ser la textura, no el fundido) */
  const preloadDestPanoramaUrl = (url) => {
    if (!url || typeof document === 'undefined') return;
    try {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
      setTimeout(() => {
        try {
          link.remove();
        } catch (_) {}
      }, 12000);
    } catch (_) {}
    try {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.src = url;
      if (typeof im.decode === 'function') im.decode().catch(() => {});
    } catch (_) {}
  };
  const runNavOpticalThenLoadScene = (v, sceneId, hotspotPitch, hotspotYaw, entryPitch, entryYaw, entryHfov, destIdx, destPanoramaUrl) => {
    if (navOpticalTimer != null) {
      clearTimeout(navOpticalTimer);
      navOpticalTimer = null;
    }
    preloadDestPanoramaUrl(destPanoramaUrl);
    const dur = transition.preLook;
    const cap = maxHfov || 120;
    const curHfov = typeof v.getHfov === 'function' ? v.getHfov() : cap;
    const targetHfov = Math.max(MIN_HFOV_NAV, Math.min(cap, curHfov * transition.preHfovFactor));
    if (typeof v.lookAt === 'function') {
      const curPitch = typeof v.getPitch === 'function' ? v.getPitch() : 0;
      const curYaw   = typeof v.getYaw   === 'function' ? v.getYaw()   : 0;
      v.lookAt(curPitch, curYaw, targetHfov, dur);
    }
    if (typeof forceHotspotsVisible === 'function') forceHotspotsVisible();
    navOpticalTimer = setTimeout(() => {
      navOpticalTimer = null;
      v.loadScene(sceneId, entryPitch, entryYaw, entryHfov);
      setImagenActual(destIdx);
      if (typeof forceHotspotsVisible === 'function') {
        forceHotspotsVisible();
        setTimeout(forceHotspotsVisible, 45);
      }
    }, dur);
  };
  // preLook = ms de zoom antes de loadScene (no bajar más sin pedirlo). preHfovFactor más alto = menos zoom
  const transition = transitionProfile === 'walk'
    ? { preLook: 55, load: 280, delay: 0, settle: 0, preHfovFactor: 0.78 }
    : { preLook: 58, load: 380, delay: 0, settle: 0, preHfovFactor: 0.88 };
  const showInfoOnClick = (html, evt) => {
    if (typeof setHotspotInfo !== 'function' || !html) return;
    const el = evt?.currentTarget || evt?.target;
    const rect = el?.getBoundingClientRect?.();
    if (rect) {
      setHotspotInfo({ html, rect: { left: rect.left, top: rect.top, width: rect.width, height: rect.height } });
    } else {
      setHotspotInfo(html);
    }
  };
  const navigateWithFade = (destIdx, destUrl, pitchOrigen, yawOrigen) => {
    if (typeof onNavigateToScene === 'function') {
      onNavigateToScene(destIdx, destUrl);
    } else if (pannellumRef.current && destUrl) {
      const yawNorm = ((yawOrigen + 180) % 360) > 180 ? ((yawOrigen + 180) % 360) - 360 : ((yawOrigen + 180) % 360) < -180 ? ((yawOrigen + 180) % 360) + 360 : (yawOrigen + 180) % 360;
      pannellumRef.current.loadScene({ type: 'equirectangular', panorama: destUrl, pitch: 0, yaw: yawNorm, hfov: 120 }, transition.load);
      setImagenActual(destIdx >= 0 ? destIdx : 0);
    }
  };

  const hotSpots = [];
  const isVideoAsset = (ruta) => {
    const src = String(ruta || '').toLowerCase();
    return src.endsWith('.webm') || src.endsWith('.mp4') || src.includes('.webm?') || src.includes('.mp4?');
  };

  const applyCssClass = (div, cssClassStr) => {
    if (!div || !cssClassStr) return;
    const root = viewerRef.current;
    if (!root) return;
    const classes = cssClassStr.split(' ').filter(c => c && c.trim());
    const addTo = (el) => { classes.forEach(c => el.classList.add(c.trim())); };
    addTo(div);
    let el = div.parentElement;
    while (el && el !== root) {
      if (el.classList && el.classList.contains('pnlm-hotspot-base')) addTo(el);
      el = el.parentElement;
    }
  };

  const mostrarHotspotYAncestros = (div) => {
    if (!div) return;
    const root = viewerRef.current;
    if (!root) return;
    let el = div;
    let depth = 0;
    while (el && el !== root && depth < 15) {
      /* NO tocar visibility ni opacity — Ca() de Pannellum los gestiona.
         Forzarlos aquí hace que los hotspots fuera de cámara aparezcan en (0,0). */
      el.style.setProperty('display', el === div ? 'flex' : 'block', 'important');
      if (el.classList && (el.classList.contains('hotspot-caja-logo') || el.classList.contains('hotspot-persona'))) {
        el.style.setProperty('pointer-events', 'none', 'important');
      } else {
        el.style.setProperty('pointer-events', 'auto', 'important');
      }
      if (el.classList && el.classList.contains('pnlm-hotspot-base')) {
        el.style.setProperty('z-index', HOTSPOT_Z, 'important');
      }
      el = el.parentElement;
      depth++;
    }
  };

  const estilosFlechaInline = (div) => {
    if (!div) return;
    div.style.setProperty('cursor', 'pointer', 'important');
    div.style.setProperty('display', 'block', 'important');
    /* NO visibility: visible — Ca() lo gestiona */
    div.style.setProperty('overflow', 'visible', 'important');
    div.innerHTML = '';
  };

  const defaultFlechaTransform = { rotateDelta: 0, scaleX: 1, scaleY: 0.62, skewX: 0, skewY: 0, perspectiveX: 0, perspectiveY: 0, offsetX: 0, offsetY: 0 };
  const parseFlechaTransform = (raw, defaultsOverride = {}) => {
    try {
      let value = raw;
      for (let i = 0; i < 2; i++) {
        if (typeof value === 'string' && value.trim()) value = JSON.parse(value);
      }
      const parsed = (value && typeof value === 'object') ? value : {};
      const defaults = { ...defaultFlechaTransform, ...defaultsOverride };
      return { ...defaults, ...parsed };
    } catch (_) {
      return { ...defaultFlechaTransform, ...defaultsOverride };
    }
  };
  const applyFlechaTransformVars = (el, rawTransform, opts = {}) => {
    if (!el) return;
    const defaultsOverride = opts.defaultScaleY != null ? { scaleY: opts.defaultScaleY } : {};
    const t = parseFlechaTransform(rawTransform, defaultsOverride);
    el.style.setProperty('--flecha-rotate-delta', `${Number(t.rotateDelta) || 0}deg`);
    el.style.setProperty('--flecha-scale-x', `${Number(t.scaleX) || 1}`);
    el.style.setProperty('--flecha-scale-y', `${t.scaleY ?? 1}`);
    el.style.setProperty('--flecha-skew-x', `${Number(t.skewX) || 0}deg`);
    el.style.setProperty('--flecha-skew-y', `${Number(t.skewY) || 0}deg`);
    const perspPxX = Number(t.perspectiveX) || 0;
    const perspPxY = Number(t.perspectiveY) || 0;
    el.style.setProperty('--flecha-perspective-x', `${perspPxX}px`);
    el.style.setProperty('--flecha-perspective-y', `${perspPxY}px`);
    el.style.setProperty('--flecha-persp-rot-x', `${-perspPxY * 0.9}deg`);
    el.style.setProperty('--flecha-persp-rot-y', `${perspPxX * 0.9}deg`);
    el.style.setProperty('--flecha-offset-x', `${Number(t.offsetX) || 0}px`);
    el.style.setProperty('--flecha-offset-y', `${Number(t.offsetY) || 0}px`);
  };

  const forceVisible = (hotSpotDiv, parent) => {
    /* NO tocar visibility ni opacity — Ca() de Pannellum los gestiona exclusivamente.
       Solo aseguramos display y pointer-events. */
    if (!viewerRef.current) return;
    if (hotSpotDiv?.classList?.contains('hotspot-caja-logo')) {
      hotSpotDiv.style.setProperty('display', 'flex', 'important');
    }
    if (!parent) return;
    while (parent && parent !== viewerRef.current) {
      if (parent.classList && parent.classList.contains('pnlm-hotspot-base')) {
        const logoFlex = parent.classList.contains('hotspot-caja-logo');
        parent.style.setProperty('display', logoFlex ? 'flex' : 'block', 'important');
      }
      parent = parent.parentElement;
    }
  };

  hotspotsDeImagen.forEach(h => {
    const pitchOrigen = Number(h.pitch ?? h.Pitch ?? 0) || 0;
    const yawOrigen = Number(h.yaw ?? h.Yaw ?? 0) || 0;
    const cssClassRaw = (h.css_class && String(h.css_class).trim()) ? String(h.css_class).trim() : '';
    const destId = h.panorama_destino_id != null ? Number(h.panorama_destino_id) : null;
    const tieneNavegacion = destId != null && centro.panoramas?.some(p => Number(p.id) === destId);
    const tieneInfo = !!(h.titulo || h.nombre || h.descripcion || h.link || h.link_panorama || h.telefono || h.telefono2 || h.direccion || h.email || (h.imagen && String(h.imagen).trim()) || (() => { try { const g = typeof h.galeria === 'string' ? JSON.parse(h.galeria) : h.galeria; return Array.isArray(g) && g.length > 0; } catch (_) { return false; } })());
    const esBurbuja = cssClassRaw.includes('hotspot-burbuja');
    const cssLower = cssClassRaw.toLowerCase();
    /** Logo siempre gana sobre "flecha": si tienes caja-logo + tipo navegacion en BD, antes se pintaba como flecha y desaparecía del sitio */
    const isLogoClass = cssLower.includes('hotspot-caja-logo') || cssLower.includes('caja-logo');
    const esFlecha = !esBurbuja && !isLogoClass && (cssClassRaw.includes('hotspot-flecha-pulse') || String(h.tipo || '').toLowerCase() === 'navegacion');
    const esPersona = cssClassRaw.includes('hotspot-persona') || String(h.tipo || '').toLowerCase() === 'persona';
    const esValla = cssClassRaw.includes('hotspot-valla') || String(h.tipo || '').toLowerCase() === 'valla';
    const esLogo = !esPersona && !esValla && (
      isLogoClass
      || (String(cssClassRaw).trim() === '' && h.imagen && String(h.imagen).trim())
    );
    const esPunto = !esFlecha && !esLogo && !esPersona && !esValla && (cssClassRaw.includes('hotspot-punto') || cssClassRaw === '');

    if (esFlecha && tieneNavegacion) {
      const dest = centro.panoramas.find(p => Number(p.id) === destId);
      const destIdx = panoramas.findIndex(p => p.id === dest.id);
      const destUrl = buildImageUrl(dest.ruta_archivo);
      const cssFlechaBase = cssClassRaw.includes('hotspot-flecha-pulse') ? cssClassRaw : (cssClassRaw ? cssClassRaw + ' hotspot-flecha-pulse' : 'hotspot-flecha-pulse');
      const cssFlecha = cssFlechaBase + ' hotspot-flecha-suelo hotspot-mas-fijo';
      const spotBase = {
        pitch: Number(pitchOrigen) || 0,
        yaw: Number(yawOrigen) || 0,
        scale: false,
        type: 'info',
        createTooltipFunc: (hotSpotDiv) => {
          if (!hotSpotDiv) return;
          applyCssClass(hotSpotDiv, cssFlecha);
          estilosFlechaInline(hotSpotDiv);
          applyFlechaTransformVars(hotSpotDiv, h.transform_json);
          mostrarHotspotYAncestros(hotSpotDiv);
          /* NO visibility/opacity — Ca() los gestiona */
          hotSpotDiv.style.setProperty('display', 'flex', 'important');
          hotSpotDiv.style.setProperty('pointer-events', 'auto', 'important');
          hotSpotDiv.style.setProperty('z-index', HOTSPOT_Z, 'important');
          let parent = hotSpotDiv.parentElement;
          let depth = 0;
          while (parent && parent !== viewerRef.current && depth < 10) {
            if (parent.classList) {
              if (parent.classList.contains('pnlm-hotspot-base') || parent.classList.contains('pnlm-hotspot')) {
                parent.style.setProperty('display', 'block', 'important');
                applyCssClass(parent, cssFlecha);
                applyCssClass(parent, 'hotspot-mas-fijo');
                applyFlechaTransformVars(parent, h.transform_json);
              }
            }
            parent = parent.parentElement;
            depth++;
          }
          const stop = (e) => { e.stopPropagation(); };
          hotSpotDiv.addEventListener('mousedown', stop);
          hotSpotDiv.addEventListener('touchstart', stop, { passive: false });
        },
        cssClass: cssFlecha,
        text: ''
      };
      if (isMultiScene) {
        const destI = destIdx >= 0 ? destIdx : 0;
        hotSpots.push({
          ...spotBase,
          clickHandlerFunc: () => {
            if (!pannellumRef.current) return;
            const v = pannellumRef.current;
            if (typeof forceHotspotsVisible === 'function') {
              forceHotspotsVisible();
              setTimeout(forceHotspotsVisible, 45);
            }
            const entry = (typeof getSceneEntryView === 'function')
              ? getSceneEntryView(dest?.id, h.panorama_id)
              : { pitch: 0, yaw: 0, hfov: (maxHfov || 120) };
            const entryPitch = Number(entry.pitch) || 0;
            const entryYaw = Number(entry.yaw) || 0;
            const entryHfov = Number(entry.hfov) || (maxHfov || 120);
            if (typeof forceHotspotsVisible === 'function') forceHotspotsVisible();
            runNavOpticalThenLoadScene(
              v,
              'scene_' + destI,
              pitchOrigen,
              yawOrigen,
              entryPitch,
              entryYaw,
              entryHfov,
              destI,
              destUrl
            );
          }
        });
      } else {
        hotSpots.push({
          ...spotBase,
          clickHandlerFunc: () => {
            if (!pannellumRef.current) return;
            navigateWithFade(destIdx >= 0 ? destIdx : 0, destUrl, pitchOrigen, yawOrigen);
          }
        });
      }
    } else if (esBurbuja && tieneNavegacion) {
      const dest = centro.panoramas.find(p => Number(p.id) === destId);
      const destIdx = panoramas.findIndex(p => p.id === dest.id);
      const destUrl = buildImageUrl(dest.ruta_archivo);
      const textoBurbuja =
        (h.titulo && String(h.titulo).trim()) ||
        (h.nombre && String(h.nombre).trim()) ||
        (dest?.nombre && String(dest.nombre).trim()) ||
        'Siguiente escena';
      const spotBase = {
        pitch: Number(pitchOrigen) || 0,
        yaw: Number(yawOrigen) || 0,
        scale: false,
        type: 'info',
        createTooltipFunc: (hotSpotDiv) => {
          if (!hotSpotDiv) return;
          applyCssClass(hotSpotDiv, 'hotspot-burbuja');
          mostrarHotspotYAncestros(hotSpotDiv);
          /* NO visibility/opacity — Ca() los gestiona */
          hotSpotDiv.style.setProperty('display', 'flex', 'important');
          hotSpotDiv.style.setProperty('alignItems', 'center', 'important');
          hotSpotDiv.style.setProperty('justifyContent', 'center', 'important');
          hotSpotDiv.style.setProperty('width', '90px', 'important');
          hotSpotDiv.style.setProperty('height', '90px', 'important');
          hotSpotDiv.style.setProperty('margin', '0', 'important');
          hotSpotDiv.style.setProperty('pointer-events', 'auto', 'important');
          hotSpotDiv.style.setProperty('cursor', 'pointer', 'important');
          hotSpotDiv.style.setProperty('overflow', 'visible', 'important');
          hotSpotDiv.setAttribute('title', textoBurbuja);
          forceVisible(hotSpotDiv, hotSpotDiv.parentElement);
          const stop = (e) => { e.stopPropagation(); };
          hotSpotDiv.addEventListener('mousedown', stop);
          hotSpotDiv.addEventListener('touchstart', stop, { passive: false });
          if (!hotSpotDiv.querySelector('.hotspot-burbuja-wrap')) {
            hotSpotDiv.innerHTML = '';
            const wrap = document.createElement('div');
            wrap.className = 'hotspot-burbuja-wrap';
            wrap.style.cssText = 'position:relative;width:100%;height:100%;overflow:visible;';
            const inner = document.createElement('div');
            inner.className = 'hotspot-burbuja-inner';
            inner.style.cssText = 'width:100%;height:100%;border-radius:50%;overflow:hidden;background:#222;border:3px solid rgba(255,255,255,0.9);box-shadow:0 0 0 2px rgba(0,0,0,0.3), inset 0 0 20px rgba(0,0,0,0.4);';
            const img = document.createElement('img');
            img.src = destUrl;
            img.alt = textoBurbuja;
            img.className = 'hotspot-burbuja-preview';
            img.style.cssText = 'width:100%;height:100%;object-fit:cover;object-position:center center;display:block;';
            img.onerror = () => { inner.innerHTML = '<span style="color:#fff;font-size:10px;display:flex;align-items:center;justify-content:center;height:100%;">→</span>'; };
            inner.appendChild(img);
            const cap = document.createElement('span');
            cap.className = 'hotspot-burbuja-caption';
            cap.textContent = textoBurbuja;
            wrap.appendChild(inner);
            wrap.appendChild(cap);
            hotSpotDiv.appendChild(wrap);
          }
        },
        cssClass: 'hotspot-burbuja',
        text: ''
      };
      if (isMultiScene) {
        const destI = destIdx >= 0 ? destIdx : 0;
        hotSpots.push({
          ...spotBase,
          clickHandlerFunc: () => {
            if (!pannellumRef.current) return;
            const v = pannellumRef.current;
            if (typeof forceHotspotsVisible === 'function') {
              forceHotspotsVisible();
              setTimeout(forceHotspotsVisible, 45);
            }
            const entry = (typeof getSceneEntryView === 'function')
              ? getSceneEntryView(dest?.id, h.panorama_id)
              : { pitch: 0, yaw: 0, hfov: (maxHfov || 120) };
            const entryPitch = Number(entry.pitch) || 0;
            const entryYaw = Number(entry.yaw) || 0;
            const entryHfov = Number(entry.hfov) || (maxHfov || 120);
            if (typeof forceHotspotsVisible === 'function') forceHotspotsVisible();
            runNavOpticalThenLoadScene(
              v,
              'scene_' + destI,
              pitchOrigen,
              yawOrigen,
              entryPitch,
              entryYaw,
              entryHfov,
              destI,
              destUrl
            );
          }
        });
      } else {
        hotSpots.push({
          ...spotBase,
          clickHandlerFunc: () => {
            if (!pannellumRef.current) return;
            navigateWithFade(destIdx >= 0 ? destIdx : 0, destUrl, pitchOrigen, yawOrigen);
          }
        });
      }
    } else if (esFlecha && !tieneNavegacion) {
      // Flecha sin destino: se muestra igual con aviso
      const cssFlechaBase = cssClassRaw.includes('hotspot-flecha-pulse') ? cssClassRaw : (cssClassRaw ? cssClassRaw + ' hotspot-flecha-pulse' : 'hotspot-flecha-pulse');
      const cssFlecha = cssFlechaBase + ' hotspot-flecha-suelo hotspot-mas-fijo';
      hotSpots.push({
        pitch: parseFloat(pitchOrigen) || 0,
        yaw: parseFloat(yawOrigen) || 0,
        type: 'info',
        scale: false,
        createTooltipFunc: (hotSpotDiv) => {
          if (!hotSpotDiv) return;
          applyCssClass(hotSpotDiv, cssFlecha);
          estilosFlechaInline(hotSpotDiv);
          applyFlechaTransformVars(hotSpotDiv, h.transform_json);
          mostrarHotspotYAncestros(hotSpotDiv);
          /* NO visibility/opacity — Ca() los gestiona */
          hotSpotDiv.style.setProperty('display', 'flex', 'important');
          hotSpotDiv.style.setProperty('pointer-events', 'auto', 'important');
          hotSpotDiv.style.setProperty('z-index', HOTSPOT_Z, 'important');
          let parent = hotSpotDiv.parentElement;
          let depth = 0;
          while (parent && parent !== viewerRef.current && depth < 10) {
            if (parent.classList && (parent.classList.contains('pnlm-hotspot-base') || parent.classList.contains('pnlm-hotspot'))) {
              parent.style.setProperty('display', 'block', 'important');
              applyCssClass(parent, cssFlecha);
              applyFlechaTransformVars(parent, h.transform_json);
            }
            parent = parent.parentElement;
            depth++;
          }
          const stop = (e) => { e.stopPropagation(); };
          hotSpotDiv.addEventListener('mousedown', stop);
          hotSpotDiv.addEventListener('touchstart', stop, { passive: false });
        },
        clickHandlerFunc: (evt) => showInfoOnClick('<div class="hotspot-tooltip-content"><p class="hotspot-tooltip-desc">Sin destino configurado. Edita en el constructor y elige «Ir a qué panorama».</p></div>', evt),
        cssClass: cssFlecha,
        text: '<div class="hotspot-tooltip-content"><p class="hotspot-tooltip-desc">Sin destino configurado. Edita en el constructor y elige «Ir a qué panorama».</p></div>'
      });
    } else if (esPunto && (tieneInfo || tieneNavegacion)) {
      const dest = tieneNavegacion ? centro.panoramas.find(p => Number(p.id) === destId) : null;
      const destIdx = dest ? panoramas.findIndex(p => p.id === dest.id) : -1;
      const destUrl = dest ? buildImageUrl(dest.ruta_archivo) : null;
      const cssPunto = cssClassRaw.includes('hotspot-punto') || cssClassRaw === '' ? (cssClassRaw || 'hotspot-punto') : cssClassRaw;
      let tooltipHTML = '';
      if (tieneInfo) {
        let galeriaArr = [];
        try { galeriaArr = typeof h.galeria === 'string' ? JSON.parse(h.galeria) : (Array.isArray(h.galeria) ? h.galeria : []); } catch (_) {}
        const allImages = galeriaArr.length > 0 ? galeriaArr : (h.imagen ? [h.imagen] : []);
        const allImagesUrls = allImages.map(u => buildImageUrl(u)).filter(Boolean);
        const galeriaUrlsAttr = allImagesUrls.length ? ` data-galeria-urls="${JSON.stringify(allImagesUrls).replace(/"/g, '&quot;')}"` : '';
        tooltipHTML = '<div class="hotspot-tooltip-content">';
        if (allImagesUrls.length > 0) {
          tooltipHTML += `<div class="hotspot-tooltip-main-img-wrap"><img src="${allImagesUrls[0]}" alt="" class="hotspot-main-img hotspot-galeria-img" loading="lazy"${galeriaUrlsAttr} data-galeria-index="0" /></div>`;
        }
        if (h.titulo) tooltipHTML += `<h4 class="hotspot-tooltip-title">${h.titulo}</h4>`;
        if (h.nombre) tooltipHTML += `<p class="hotspot-tooltip-name">${h.nombre}</p>`;
        if (h.descripcion) tooltipHTML += `<p class="hotspot-tooltip-desc">${h.descripcion}</p>`;
        if (h.telefono) tooltipHTML += `<p class="hotspot-tooltip-info">📞 ${h.telefono}</p>`;
        if (h.telefono2) tooltipHTML += `<p class="hotspot-tooltip-info">📞 ${h.telefono2}</p>`;
        if (h.direccion) tooltipHTML += `<p class="hotspot-tooltip-info">📍 ${h.direccion}</p>`;
        if (h.email) tooltipHTML += `<p class="hotspot-tooltip-info">✉ <a href="mailto:${String(h.email).replace(/&/g, '&amp;')}" class="hotspot-tooltip-link">${h.email}</a></p>`;
        if (h.link) tooltipHTML += `<a href="${String(h.link).replace(/&/g, '&amp;').replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer" class="hotspot-tooltip-link">Visitar sitio →</a>`;
        if (h.link_panorama) tooltipHTML += `<a href="${String(h.link_panorama).replace(/&/g, '&amp;').replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer" class="hotspot-tooltip-entrar">ENTRAR</a>`;
        if (allImagesUrls.length > 1) {
          tooltipHTML += '<div class="hotspot-tooltip-galeria">';
          allImagesUrls.forEach((url, idx) => { tooltipHTML += `<img src="${url}" alt="" class="hotspot-galeria-img" loading="lazy"${galeriaUrlsAttr} data-galeria-index="${idx}" />`; });
          tooltipHTML += '</div>';
        }
        tooltipHTML += '</div>';
      }
      hotSpots.push({
        pitch: parseFloat(pitchOrigen) || 0,
        yaw: parseFloat(yawOrigen) || 0,
        type: 'info',
        scale: false,
        createTooltipFunc: (hotSpotDiv) => {
          if (!hotSpotDiv) return;
          applyCssClass(hotSpotDiv, cssPunto);
          mostrarHotspotYAncestros(hotSpotDiv);
          /* NO visibility/opacity — Ca() los gestiona */
          hotSpotDiv.style.setProperty('display', 'block', 'important');
          hotSpotDiv.style.setProperty('pointer-events', 'auto', 'important');
          forceVisible(hotSpotDiv, hotSpotDiv.parentElement);
          const stop = (e) => { e.stopPropagation(); };
          hotSpotDiv.addEventListener('mousedown', stop);
          hotSpotDiv.addEventListener('touchstart', stop, { passive: false });
        },
        clickHandlerFunc: (evt) => {
          if (tieneInfo && tooltipHTML) showInfoOnClick(tooltipHTML, evt);
          if (tieneNavegacion && destIdx >= 0 && destUrl)
            navigateWithFade(destIdx, destUrl, pitchOrigen, yawOrigen);
        },
        cssClass: cssPunto,
        text: tooltipHTML || h.nombre || h.titulo || ''
      });
    } else if (esValla && h.imagen) {
      const imagenUrl = buildImageUrl(h.imagen);
      const ancho = Number(h.ancho) || 300;
      const alto = Number(h.alto) || 200;
      const totalAltura = alto + 578;
      const scaleVisual = Math.max(1, Math.min(5, Number(h.scale_visual) || 2.5));
      const linkUrl = (h.link && String(h.link).trim()) ? String(h.link).trim() : null;
      const BASE_SCALE = 0.4;
      const scaleFinal = BASE_SCALE * scaleVisual;
      const cssValla = 'hotspot-valla hotspot-mas-fijo hotspot-valla-html';
      // scale: true = Pannellum escala el hotspot con el zoom, evita descolocarse al zoom con rueda
      hotSpots.push({
        pitch: parseFloat(pitchOrigen) || 0,
        yaw: parseFloat(yawOrigen) || 0,
        type: 'info',
        scale: true,
        createTooltipFunc: (hotSpotDiv) => {
          if (!hotSpotDiv) return;
          applyCssClass(hotSpotDiv, cssValla);
          applyFlechaTransformVars(hotSpotDiv, h.transform_json);
          hotSpotDiv.style.setProperty('--valla-scale-final', String(scaleFinal), 'important');
          hotSpotDiv.style.setProperty('--valla-ancho', String(ancho), 'important');
          hotSpotDiv.style.setProperty('--valla-total-altura', String(totalAltura), 'important');
          hotSpotDiv.style.setProperty('--valla-compensate-x', `${(ancho - 118) / 2}px`, 'important');
          hotSpotDiv.style.setProperty('--valla-compensate-y', `${(totalAltura - 62) / 2}px`, 'important');
          let pValla = hotSpotDiv.parentElement;
          let dValla = 0;
          while (pValla && pValla !== viewerRef.current && dValla < 10) {
            if (pValla.classList && (pValla.classList.contains('pnlm-hotspot-base') || pValla.classList.contains('pnlm-hotspot'))) {
              applyFlechaTransformVars(pValla, h.transform_json);
            }
            pValla = pValla.parentElement;
            dValla++;
          }
          mostrarHotspotYAncestros(hotSpotDiv);
          /* NO visibility/opacity — Ca() los gestiona */
          hotSpotDiv.style.setProperty('display', 'block', 'important');
          hotSpotDiv.style.setProperty('pointer-events', linkUrl ? 'auto' : 'none', 'important');
          hotSpotDiv.style.setProperty('cursor', linkUrl ? 'pointer' : 'default', 'important');
          hotSpotDiv.style.setProperty('transform-origin', 'center center', 'important');
          // 118×62 para estabilidad frente a drift; margin para centrar
          hotSpotDiv.style.setProperty('width', '118px', 'important');
          hotSpotDiv.style.setProperty('height', '62px', 'important');
          hotSpotDiv.style.setProperty('min-width', '118px', 'important');
          hotSpotDiv.style.setProperty('max-width', '118px', 'important');
          hotSpotDiv.style.setProperty('min-height', '62px', 'important');
          hotSpotDiv.style.setProperty('max-height', '62px', 'important');
          hotSpotDiv.style.setProperty('margin', '-31px 0 0 -59px', 'important');
          hotSpotDiv.style.setProperty('overflow', 'visible', 'important');
          forceVisible(hotSpotDiv, hotSpotDiv.parentElement);
          const stop = (e) => { e.stopPropagation(); };
          hotSpotDiv.addEventListener('mousedown', stop);
          hotSpotDiv.addEventListener('touchstart', stop, { passive: false });
          if (!hotSpotDiv.querySelector('.hotspot-valla-panel')) {
            hotSpotDiv.innerHTML = '';
            const contenido = document.createElement('div');
            contenido.className = 'hotspot-valla-contenido';
            const marco = document.createElement('div');
            marco.className = 'hotspot-valla-marco';
            marco.style.setProperty('pointer-events', 'auto', 'important');
            const panel = document.createElement('div');
            panel.className = 'hotspot-valla-panel';
            panel.style.cssText = `width:${ancho}px;height:${alto}px;`;
            const img = document.createElement('img');
            img.src = imagenUrl;
            img.alt = h.nombre || h.titulo || 'Marquesina';
            img.className = 'hotspot-valla-img';
            img.loading = 'eager';
            img.onerror = () => { panel.innerHTML = '<span style="color:#333;font-size:11px;">Marquesina</span>'; };
            panel.appendChild(img);
            marco.appendChild(panel);
            contenido.appendChild(marco);
            const pilar = document.createElement('div');
            pilar.className = 'hotspot-valla-pilar';
            contenido.appendChild(pilar);
            const base = document.createElement('div');
            base.className = 'hotspot-valla-base';
            contenido.appendChild(base);
            hotSpotDiv.appendChild(contenido);
          }
        },
        clickHandlerFunc: () => {
          if (linkUrl) {
            window.open(linkUrl, '_blank', 'noopener,noreferrer');
          }
        },
        cssClass: cssValla,
        text: ''
      });
    } else if (esLogo) {
      const imagenUrl = h.imagen ? buildImageUrl(h.imagen) : null;
      let tooltipHTML = '';
      if (tieneInfo) {
        let galeriaArrLogo = [];
        try { galeriaArrLogo = typeof h.galeria === 'string' ? JSON.parse(h.galeria) : (Array.isArray(h.galeria) ? h.galeria : []); } catch (_) {}
        const allImagesLogo = galeriaArrLogo.length > 0 ? galeriaArrLogo : (h.imagen ? [h.imagen] : []);
        const allImagesUrlsLogo = allImagesLogo.map(u => buildImageUrl(u)).filter(Boolean);
        const galeriaUrlsAttrLogo = allImagesUrlsLogo.length ? ` data-galeria-urls="${JSON.stringify(allImagesUrlsLogo).replace(/"/g, '&quot;')}"` : '';
        tooltipHTML = '<div class="hotspot-tooltip-content">';
        if (allImagesUrlsLogo.length > 0) {
          tooltipHTML += `<div class="hotspot-tooltip-main-img-wrap"><img src="${allImagesUrlsLogo[0]}" alt="" class="hotspot-main-img hotspot-galeria-img" loading="lazy"${galeriaUrlsAttrLogo} data-galeria-index="0" /></div>`;
        }
        if (h.titulo) tooltipHTML += `<h4 class="hotspot-tooltip-title">${h.titulo}</h4>`;
        if (h.nombre) tooltipHTML += `<p class="hotspot-tooltip-name">${h.nombre}</p>`;
        if (h.descripcion) tooltipHTML += `<p class="hotspot-tooltip-desc">${h.descripcion}</p>`;
        if (h.telefono) tooltipHTML += `<p class="hotspot-tooltip-info">📞 ${h.telefono}</p>`;
        if (h.telefono2) tooltipHTML += `<p class="hotspot-tooltip-info">📞 ${h.telefono2}</p>`;
        if (h.direccion) tooltipHTML += `<p class="hotspot-tooltip-info">📍 ${h.direccion}</p>`;
        if (h.email) tooltipHTML += `<p class="hotspot-tooltip-info">✉ <a href="mailto:${String(h.email).replace(/&/g, '&amp;')}" class="hotspot-tooltip-link">${h.email}</a></p>`;
        if (h.link) tooltipHTML += `<a href="${String(h.link).replace(/&/g, '&amp;').replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer" class="hotspot-tooltip-link">Visitar sitio →</a>`;
        if (h.link_panorama) tooltipHTML += `<a href="${String(h.link_panorama).replace(/&/g, '&amp;').replace(/"/g, '&quot;')}" target="_blank" rel="noopener noreferrer" class="hotspot-tooltip-entrar">ENTRAR</a>`;
        if (allImagesUrlsLogo.length > 1) {
          tooltipHTML += '<div class="hotspot-tooltip-galeria">';
          allImagesUrlsLogo.forEach((url, idx) => { tooltipHTML += `<img src="${url}" alt="" class="hotspot-galeria-img" loading="lazy"${galeriaUrlsAttrLogo} data-galeria-index="${idx}" />`; });
          tooltipHTML += '</div>';
        }
        tooltipHTML += '</div>';
      }
      // Logo = imagen con ancho/alto editable + transform (escala, rotar, sesgar, mover)
      const cssLogoComoPunto = 'hotspot-punto hotspot-caja-logo';
      const logoAncho = Number(h.ancho) || 560;
      const logoAlto = Number(h.alto) || 300;
      hotSpots.push({
        id: h.id != null ? `logo-${h.id}` : undefined,
        pitch: parseFloat(pitchOrigen) || 0,
        yaw: parseFloat(yawOrigen) || 0,
        type: 'info',
        scale: false,
        createTooltipFunc: (hotSpotDiv) => {
          if (!hotSpotDiv) return;
          applyCssClass(hotSpotDiv, cssLogoComoPunto);
          hotSpotDiv.style.setProperty('width', `${logoAncho}px`, 'important');
          hotSpotDiv.style.setProperty('height', `${logoAlto}px`, 'important');
          hotSpotDiv.style.setProperty('min-width', `${logoAncho}px`, 'important');
          hotSpotDiv.style.setProperty('min-height', `${logoAlto}px`, 'important');
          /* Sin margen negativo: Pannellum ya centra con offsetWidth/Height (ver Ca en pannellum.js) */
          hotSpotDiv.style.setProperty('margin', '0', 'important');
          applyFlechaTransformVars(hotSpotDiv, h.transform_json, { defaultScaleY: 1 });
          /* Pannellum pasa aquí el propio .pnlm-hotspot-base; hay que incluirlo en la cadena (antes solo se tocaban padres) */
          let pLogo = hotSpotDiv;
          let dLogo = 0;
          while (pLogo && pLogo !== viewerRef.current && dLogo < 10) {
            if (pLogo.classList && (pLogo.classList.contains('pnlm-hotspot-base') || pLogo.classList.contains('pnlm-hotspot'))) {
              pLogo.style.setProperty('width', `${logoAncho}px`, 'important');
              pLogo.style.setProperty('height', `${logoAlto}px`, 'important');
              pLogo.style.setProperty('min-width', `${logoAncho}px`, 'important');
              pLogo.style.setProperty('min-height', `${logoAlto}px`, 'important');
              pLogo.style.setProperty('margin', '0', 'important');
              pLogo.style.setProperty('display', 'flex', 'important');
              pLogo.style.setProperty('align-items', 'center', 'important');
              pLogo.style.setProperty('justify-content', 'center', 'important');
              applyFlechaTransformVars(pLogo, h.transform_json, { defaultScaleY: 1 });
            }
            pLogo = pLogo.parentElement;
            dLogo++;
          }
          mostrarHotspotYAncestros(hotSpotDiv);
          /* NO visibility/opacity — Ca() los gestiona */
          hotSpotDiv.style.setProperty('display', 'flex', 'important');
          hotSpotDiv.style.setProperty('align-items', 'center', 'important');
          hotSpotDiv.style.setProperty('justify-content', 'center', 'important');
          hotSpotDiv.style.setProperty('pointer-events', 'none', 'important');
          hotSpotDiv.style.setProperty('overflow', 'visible', 'important');
          forceVisible(hotSpotDiv, hotSpotDiv.parentElement);
          /* No stopPropagation en mousedown: permitir arrastrar el panorama; el clic lo recibe el faldón/logo (pointer-events) */
          if (imagenUrl && !hotSpotDiv.querySelector('.logo-stand-shell')) {
            hotSpotDiv.innerHTML = '';
            const shell = document.createElement('div');
            shell.className = 'logo-stand-shell';
            /* NO visibility inline en hijos: heredan del padre (.pnlm-hotspot-base).
               Si el hijo tiene visibility:visible explícita, sobrepasa la visibility:hidden
               del padre y el logo aparece flotando en (0,0) antes de que Ca() lo posicione. */
            const bgImg = document.createElement('img');
            bgImg.className = 'logo-stand-bg-img';
            bgImg.src = standBackgroundUrl;
            bgImg.alt = '';
            bgImg.setAttribute('aria-hidden', 'true');
            bgImg.draggable = false;
            bgImg.onload = () => {
              if (typeof onHotspotLayoutRefresh === 'function') onHotspotLayoutRefresh();
            };
            const fascia = document.createElement('div');
            fascia.className = 'logo-stand-fascia';
            const contenido = document.createElement('div');
            contenido.className = 'logo-persona-contenido logo-relleno';
            contenido.style.setProperty('display', 'flex', 'important');
            const img = document.createElement('img');
            img.src = imagenUrl;
            img.alt = h.nombre || h.titulo || 'Logo';
            img.className = 'hotspot-caja-img';
            img.style.setProperty('display', 'block', 'important');
            img.loading = 'eager';
            img.onerror = () => { contenido.innerHTML = '<span style="color: #666; font-size: 12px;">Logo</span>'; };
            img.onload = () => {
              if (typeof onHotspotLayoutRefresh === 'function') onHotspotLayoutRefresh();
            };
            contenido.appendChild(img);
            fascia.appendChild(contenido);
            const base = document.createElement('div');
            base.className = 'logo-stand-base';
            base.setAttribute('aria-hidden', 'true');
            shell.appendChild(bgImg);
            shell.appendChild(fascia);
            shell.appendChild(base);
            hotSpotDiv.appendChild(shell);
            shell.style.setProperty('pointer-events', 'none', 'important');
            fascia.style.setProperty('pointer-events', 'auto', 'important');
            fascia.style.setProperty('cursor', 'pointer', 'important');
            img.style.setProperty('pointer-events', 'auto', 'important');
          } else if (!imagenUrl && !hotSpotDiv.textContent) {
            hotSpotDiv.innerHTML = '';
            const shell = document.createElement('div');
            shell.className = 'logo-stand-shell';
            const bgImg = document.createElement('img');
            bgImg.className = 'logo-stand-bg-img';
            bgImg.src = standBackgroundUrl;
            bgImg.alt = '';
            bgImg.setAttribute('aria-hidden', 'true');
            bgImg.draggable = false;
            const fascia = document.createElement('div');
            fascia.className = 'logo-stand-fascia';
            fascia.innerHTML = '<span style="color:#888;font-size:11px;">Logo</span>';
            const base = document.createElement('div');
            base.className = 'logo-stand-base';
            base.setAttribute('aria-hidden', 'true');
            shell.appendChild(bgImg);
            shell.appendChild(fascia);
            shell.appendChild(base);
            hotSpotDiv.appendChild(shell);
            shell.style.setProperty('pointer-events', 'none', 'important');
            fascia.style.setProperty('pointer-events', 'auto', 'important');
            fascia.style.setProperty('cursor', 'pointer', 'important');
          }
        },
        clickHandlerFunc: (evt) => {
          const content = tooltipHTML || (h.nombre || h.titulo ? `<div class="hotspot-tooltip-content"><p class="hotspot-tooltip-name">${h.nombre || h.titulo}</p></div>` : (imagenUrl ? '' : 'Sube una imagen en el constructor.'));
          if (content) showInfoOnClick(content, evt);
        },
        cssClass: cssLogoComoPunto,
        text: tooltipHTML || h.nombre || h.titulo || (imagenUrl ? '' : 'Sube una imagen en el constructor.')
      });
    } else if (esPersona) {
      const imagenUrl = h.imagen ? buildImageUrl(h.imagen) : '';
      const esVideoPersona = isVideoAsset(h.imagen);
      let personaTrRaw = {};
      try {
        let v = h.transform_json;
        for (let i = 0; i < 2; i++) {
          if (typeof v === 'string' && v.trim()) v = JSON.parse(v);
        }
        if (v && typeof v === 'object') personaTrRaw = v;
      } catch (_) {}
      const personaTransformNorm = normalizePersonaTransformFromDb(personaTrRaw);
      const cssPersonaBase = cssClassRaw.includes('hotspot-persona') ? cssClassRaw : (cssClassRaw ? cssClassRaw + ' hotspot-persona' : 'hotspot-persona');
      const cssPersona = cssPersonaBase.includes('hotspot-mas-fijo') ? cssPersonaBase : `${cssPersonaBase} hotspot-mas-fijo`;
      const personaAncho = Number(h.ancho) || 220;
      const personaAlto = Number(h.alto) || 360;
      let tooltipHTML = '';
      if (h.titulo || h.nombre || h.descripcion) {
        tooltipHTML = '<div class="hotspot-tooltip-content">';
        if (h.titulo) tooltipHTML += `<h4 class="hotspot-tooltip-title">${h.titulo}</h4>`;
        if (h.nombre) tooltipHTML += `<p class="hotspot-tooltip-name">${h.nombre}</p>`;
        if (h.descripcion) tooltipHTML += `<p class="hotspot-tooltip-desc">${h.descripcion}</p>`;
        tooltipHTML += '</div>';
      }
      hotSpots.push({
        pitch: pitchOrigen,
        yaw: yawOrigen,
        type: 'info',
        scale: false,
        createTooltipFunc: (hotSpotDiv) => {
          if (!hotSpotDiv) return;
          applyCssClass(hotSpotDiv, cssPersona);
          applyFlechaTransformVars(hotSpotDiv, personaTransformNorm, { defaultScaleY: 1 });
          hotSpotDiv.style.setProperty('width', `${personaAncho}px`, 'important');
          hotSpotDiv.style.setProperty('height', `${personaAlto}px`, 'important');
          hotSpotDiv.style.setProperty('min-width', `${personaAncho}px`, 'important');
          hotSpotDiv.style.setProperty('min-height', `${personaAlto}px`, 'important');
          hotSpotDiv.style.setProperty('margin', '0', 'important');
          hotSpotDiv.style.setProperty('pointer-events', 'none', 'important');
          let pPersona = hotSpotDiv;
          let dPersona = 0;
          while (pPersona && pPersona !== viewerRef.current && dPersona < 10) {
            if (pPersona.classList && (pPersona.classList.contains('pnlm-hotspot-base') || pPersona.classList.contains('pnlm-hotspot'))) {
              pPersona.style.setProperty('width', `${personaAncho}px`, 'important');
              pPersona.style.setProperty('height', `${personaAlto}px`, 'important');
              pPersona.style.setProperty('min-width', `${personaAncho}px`, 'important');
              pPersona.style.setProperty('min-height', `${personaAlto}px`, 'important');
              pPersona.style.setProperty('margin', '0', 'important');
              pPersona.style.setProperty('display', 'flex', 'important');
              pPersona.style.setProperty('align-items', 'flex-end', 'important');
              pPersona.style.setProperty('justify-content', 'center', 'important');
              applyFlechaTransformVars(pPersona, personaTransformNorm, { defaultScaleY: 1 });
            }
            pPersona = pPersona.parentElement;
            dPersona++;
          }
          mostrarHotspotYAncestros(hotSpotDiv);
          /* NO visibility/opacity — Ca() los gestiona */
          hotSpotDiv.style.setProperty('display', 'flex', 'important');
          hotSpotDiv.style.setProperty('align-items', 'flex-end', 'important');
          hotSpotDiv.style.setProperty('justify-content', 'center', 'important');
          forceVisible(hotSpotDiv, hotSpotDiv.parentElement);
          if (!hotSpotDiv.querySelector('.logo-persona-contenido')) {
            hotSpotDiv.innerHTML = '';
            if (!imagenUrl) {
              hotSpotDiv.innerHTML = '<span style="color:#fff;font-size:11px;background:rgba(0,0,0,.35);padding:2px 6px;border-radius:4px;">Persona</span>';
            } else {
              const contenido = document.createElement('div');
              contenido.className = 'logo-persona-contenido';
              if (esVideoPersona) {
                const video = document.createElement('video');
                video.src = imagenUrl;
                video.className = 'hotspot-persona-video';
                video.autoplay = true;
                video.loop = true;
                video.muted = true;
                video.playsInline = true;
                video.preload = 'auto';
                video.setAttribute('crossorigin', 'anonymous');
                video.play?.().catch(() => {});
                video.onerror = () => { contenido.innerHTML = '<span style="color:#fff;font-size:11px;">Video</span>'; };
                video.addEventListener('loadeddata', () => {
                  if (typeof onHotspotLayoutRefresh === 'function') onHotspotLayoutRefresh();
                });
                contenido.appendChild(video);
              } else {
                const img = document.createElement('img');
                img.src = imagenUrl;
                img.alt = h.nombre || h.titulo || 'Persona';
                img.className = 'hotspot-persona-img';
                img.onerror = () => { contenido.innerHTML = '<span style="color:#fff;font-size:11px;">Persona</span>'; };
                img.onload = () => {
                  if (typeof onHotspotLayoutRefresh === 'function') onHotspotLayoutRefresh();
                };
                contenido.appendChild(img);
              }
              hotSpotDiv.appendChild(contenido);
              contenido.style.setProperty('pointer-events', 'auto', 'important');
              contenido.style.setProperty('cursor', 'pointer', 'important');
            }
          }
          const pe = hotSpotDiv.querySelector('.logo-persona-contenido');
          if (pe?.style) {
            pe.style.setProperty('pointer-events', 'auto', 'important');
            pe.style.setProperty('cursor', 'pointer', 'important');
          }
        },
        clickHandlerFunc: (evt) => {
          if (tooltipHTML) showInfoOnClick(tooltipHTML, evt);
        },
        cssClass: cssPersona,
        text: tooltipHTML || ''
      });
    } else if (h.tipo === 'informativo') {
      hotSpots.push({
        pitch: pitchOrigen,
        yaw: yawOrigen,
        type: 'info',
        text: h.nombre + (h.descripcion ? '\n' + h.descripcion : ''),
        cssClass: 'hotspot-info'
      });
    } else {
      // Cualquier otro hotspot: mostrarlo como punto para que siempre sea visible en el recorrido
      let tooltipHTML = '';
      if (tieneInfo) {
        tooltipHTML = '<div class="hotspot-tooltip-content">';
        if (h.titulo) tooltipHTML += `<h4 class="hotspot-tooltip-title">${h.titulo}</h4>`;
        if (h.nombre) tooltipHTML += `<p class="hotspot-tooltip-name">${h.nombre}</p>`;
        if (h.descripcion) tooltipHTML += `<p class="hotspot-tooltip-desc">${h.descripcion}</p>`;
        if (h.link) tooltipHTML += `<a href="${h.link}" target="_blank" class="hotspot-tooltip-link">Visitar →</a>`;
        tooltipHTML += '</div>';
      }
      hotSpots.push({
        pitch: parseFloat(pitchOrigen) || 0,
        yaw: parseFloat(yawOrigen) || 0,
        type: 'info',
        scale: false,
        createTooltipFunc: (hotSpotDiv) => {
          if (!hotSpotDiv) return;
          applyCssClass(hotSpotDiv, 'hotspot-punto');
          mostrarHotspotYAncestros(hotSpotDiv);
          /* NO visibility/opacity — Ca() los gestiona */
          hotSpotDiv.style.setProperty('display', 'block', 'important');
          hotSpotDiv.style.setProperty('pointer-events', 'auto', 'important');
          forceVisible(hotSpotDiv, hotSpotDiv.parentElement);
          const stop = (e) => { e.stopPropagation(); };
          hotSpotDiv.addEventListener('mousedown', stop);
          hotSpotDiv.addEventListener('touchstart', stop, { passive: false });
        },
        clickHandlerFunc: (evt) => showInfoOnClick(tooltipHTML || h.nombre || h.titulo || '', evt),
        cssClass: 'hotspot-punto',
        text: tooltipHTML || h.nombre || h.titulo || ''
      });
    }
  });

  tiendasDeImagen.forEach(t => {
    hotSpots.push({
      pitch: parseFloat(t.pitch),
      yaw: parseFloat(t.yaw),
      type: 'info',
      scale: false,
      text: t.nombre,
      createTooltipFunc: (hotSpotDiv) => {
        if (!hotSpotDiv) return;
        applyCssClass(hotSpotDiv, 'hotspot-tienda');
        mostrarHotspotYAncestros(hotSpotDiv);
        /* NO visibility/opacity — Ca() los gestiona */
        hotSpotDiv.style.setProperty('display', 'block', 'important');
        hotSpotDiv.style.setProperty('pointer-events', 'auto', 'important');
        forceVisible(hotSpotDiv, hotSpotDiv.parentElement);
        const stop = (e) => { e.stopPropagation(); };
        hotSpotDiv.addEventListener('mousedown', stop);
        hotSpotDiv.addEventListener('touchstart', stop, { passive: false });
      },
      clickHandlerFunc: () => setInfoTienda(t),
      cssClass: 'hotspot-tienda'
    });
  });

  return hotSpots;
}
